<script lang="ts" setup>
// Composables
const { getActionAttributes } = useApiAttributes()
const { t } = useI18n()

const endpoints: ApiEndpoint[] = [
  {
    method: 'POST',
    path: '/actions',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.domains'),
    example: ['pearson.com', 'united.com'],
    maxLength: 100,
    name: 'domains',
    optional: true,
    type: 'array',
  },
  {
    description: t('attributes.estimate'),
    name: 'estimate',
    optional: true,
    type: 'boolean',
    values: ['false', 'true'],
  },
  {
    description: t('attributes.job'),
    name: 'job',
    optional: true,
    type: 'string',
    values: ['enrich-companies', 'enrich-list'],
  },
  {
    description: t('attributes.listId'),
    example: 42,
    name: 'list',
    optional: true,
    type: 'number',
  },
]

const response = {
  actions: [
    {
      cost: 10,
      createdAt: '2024-08-23T17:02:47.296+02:00',
      data: {
        domains: ['pearson.com', 'united.com'],
        job: 'enrich-companies',
      },
      status: 'pending',
      type: 'jobs:request',
    },
  ],
  estimate: true,
}

const responseAttributes = computed((): ApiAttribute[] => {
  return [
    {
      attributes: getActionAttributes(),
      description: t('response.actions'),
      name: 'actions',
      type: 'array',
    },
    {
      description: t('response.estimate'),
      name: 'estimate',
      type: 'boolean',
    },
  ]
})
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :level-to-open="2"
    :query-attributes="queryAttributes"
    :response="response"
    :response-attributes="responseAttributes"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "domains": "The domains that need to be processed. If you pass more than 100K domains, the request will be split into multiple actions.",
      "estimate": "Indicates whether you are requesting a price estimate.",
      "job": "The type of task to be performed.",
      "listId": "The ID of the list to be processed."
    },
    "response": {
      "actions": "The actions that have been triggered by the request.",
      "estimate": "Indicates whether you are requesting a price estimate."
    }
  },
  "es": {
    "attributes": {
      "domains": "Los dominios que necesitan ser procesados. Si pasas más de 100K dominios, la solicitud se dividirá en múltiples acciones.",
      "estimate": "Indica si estás solicitando una estimación de precio.",
      "job": "El tipo de tarea a realizar.",
      "listId": "El ID de la lista a procesar."
    },
    "response": {
      "actions": "Las acciones que han sido desencadenadas por la solicitud.",
      "estimate": "Indica si estás solicitando una estimación de precio."
    }
  },
  "fr": {
    "attributes": {
      "domains": "Les domaines à traiter. Si vous envoyez plus de 100K domaines, la requête sera divisée en plusieurs actions.",
      "estimate": "Indique si vous demandez une estimation de prix.",
      "job": "Le type de tâche à effectuer.",
      "listId": "L'ID de la liste à traiter."
    },
    "response": {
      "actions": "Les actions déclenchées par la demande.",
      "estimate": "Indique si vous demandez une estimation de prix."
    }
  }
}
</i18n>
